import {
  CheckSuccessCircleIcon,
  InformationalBanner,
  MoneyBankNotesIcon,
  WarningIcon,
} from '@wonderschool/common-base-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
// eslint-disable-next-line no-restricted-imports
import { Dimmer, Loader } from 'semantic-ui-react';

import { getCreatePayoutAccountUrl } from '../../api/firebase/organizations';
import { buildHelpCenterLink } from '../../navigation/navigationUtils';
import { paths } from '../../navigation/paths';
import { useSegmentTrack } from '../../segment';

type SetupBannerType = 'error' | 'warning' | 'success';

interface SetupBannerProps {
  type: SetupBannerType;
  message: string;
  organizationId: string;
  onError?: (error: string) => void;
  showLink?: boolean;
  isDismissable?: boolean;
  segmentEvent?: string;
  onDismiss?: () => void;
  items?: string[];
  linkText?: string;
  showHelpLink?: boolean;
  isMessageBold?: boolean;
}

const iconByType: Record<SetupBannerType, React.ReactNode> = {
  error: <WarningIcon className="h-5 w-5" />,
  warning: <MoneyBankNotesIcon className="h-5 w-5" />,
  success: <CheckSuccessCircleIcon className="h-5 w-5" />,
};

export default function SetupBanner(props: SetupBannerProps) {
  const {
    type,
    message,
    organizationId,
    onError,
    showLink = false,
    isDismissable = false,
    segmentEvent,
    onDismiss,
    items = [],
    linkText,
    showHelpLink,
  } = props;

  const [isRequestInProgress, setIsRequestInProgress] = React.useState(false);

  const { t } = useTranslation();
  const segmentTrack = useSegmentTrack();

  const handleSetupClick = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setIsRequestInProgress(true);
    try {
      if (segmentEvent) {
        segmentTrack(segmentEvent);
      }
      const setupPayoutUri = await getCreatePayoutAccountUrl(organizationId);
      window.location.href = setupPayoutUri;
    } catch (error) {
      console.error(error);
      setIsRequestInProgress(false);

      const errorMesagge = error instanceof Error ? error.message : String(error);
      onError && onError(errorMesagge);
    }
  };

  const hasItems = items.length > 0;

  // InformationalBanner renders `label ?? children` (one or the other, never both),
  // so the message, the items list and the help link are composed into a single
  // `label` node rather than split across `label` and `children`.
  const label = (
    <div>
      <div>{message}</div>
      {hasItems && (
        <ul className="list-disc pl-5">
          {items.map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </ul>
      )}
      {showHelpLink ? (
        <Link to={{ pathname: buildHelpCenterLink(paths.external.helpCenter) }} target="_blank">
          {t('setupPayoutBanner.helpLink')}
        </Link>
      ) : null}
    </div>
  );

  return (
    <>
      {isRequestInProgress && (
        <Dimmer page active inverted>
          <Loader inverted size="large" content={t('setupPayoutBanner.loadingMessage')} />
        </Dimmer>
      )}
      <div className="my-4">
        <InformationalBanner
          type={type}
          icon={iconByType[type]}
          label={label}
          {...(showLink
            ? {
                linkLabel: linkText || t('setupPayoutBanner.setupButton'),
                onClickLink: handleSetupClick,
              }
            : {})}
          {...(isDismissable ? { onClose: () => onDismiss && onDismiss() } : {})}
        />
      </div>
    </>
  );
}
